import React, { useState, useRef, useEffect } from 'react';
import { clearPersistedViewState } from '@/hooks/usePersistedState';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, List, Settings, LogOut, ChevronRight, ChevronDown, BarChart3, FileText, Shield, Lock, Moon, Sun, CheckSquare, Heart, Lightbulb, MessageSquare, Palette, Vote, Megaphone, Users, Trophy, BookOpen, Eye, Bot, FolderKanban, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';
import { useWebSocket } from '@/contexts/WebSocketContext';
import { useLanguage } from '@/context/LanguageContext';

export const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [ibExpanded, setIbExpanded] = useState(true);
  const [teamExpanded, setTeamExpanded] = useState(true);
  const { isDark, toggleTheme } = useTheme();
  const { t } = useLanguage();
  const { onlineUsers } = useWebSocket() || {};
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  
  // Build online users list including self
  const allOnline = onlineUsers || [];
  const selfIncluded = allOnline.some(u => u.user_id === currentUser.id);
  const displayOnline = selfIncluded ? allOnline : [
    { user_id: currentUser.id, name: currentUser.name || 'You', avatar_url: currentUser.avatar_url },
    ...allOnline
  ];
  
  // Secret admin access - tap logo 6x
  const [tapCount, setTapCount] = useState(0);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const tapTimeoutRef = useRef(null);
  
  const DEFAULT_PIN = '123456';

  // Handle logo tap for secret admin access - NO visual feedback for security
  const handleLogoTap = () => {
    setTapCount(prev => {
      const newCount = prev + 1;
      
      // Clear existing timeout
      if (tapTimeoutRef.current) {
        clearTimeout(tapTimeoutRef.current);
      }
      
      // Reset after 3 seconds of inactivity
      tapTimeoutRef.current = setTimeout(() => {
        setTapCount(0);
      }, 3000);
      
      // Show PIN modal after 6 taps - NO visual hints
      if (newCount >= 6) {
        setShowPinModal(true);
        setTapCount(0);
        return 0;
      }
      
      return newCount;
    });
  };

  // Handle PIN verification
  const handlePinSubmit = () => {
    const storedPin = localStorage.getItem('adminPin') || DEFAULT_PIN;
    if (pinInput === storedPin) {
      setShowPinModal(false);
      setPinInput('');
      toast.success('Akses Admin diberikan!');
      navigate('/admin-panel');
    } else {
      toast.error('PIN salah');
      setPinInput('');
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (tapTimeoutRef.current) {
        clearTimeout(tapTimeoutRef.current);
      }
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('selectedIB');
    clearPersistedViewState();
    window.location.assign('/login');
  };

  const isIBManagementActive = location.pathname.includes('/ib-management') || location.pathname.includes('/ib/');
  const isTeamActive = location.pathname.includes('/team/') || location.pathname === '/my-tasks';

  const teamItems = [
    { path: '/my-tasks', label: t('myTasks'), icon: CheckSquare },
    { path: '/projects', label: 'Project Management', icon: FolderKanban },
    { path: '/calendar', label: 'Team Calendar', icon: CalendarDays },
    { path: '/team/announcements', label: t('announcements'), icon: Megaphone },
    { path: '/team/standup', label: t('weeklyStandup'), icon: Users },
    { path: '/team/ideas-feedback', label: 'Ideas & Feedback', icon: Lightbulb },
    { path: '/team/creative-request', label: t('creativeRequest'), icon: Palette },
    { path: '/team/voting', label: t('votingPolls'), icon: Vote },
    { path: '/team/mood', label: t('dailyEcstasy'), icon: Heart },
    { path: '/team/leaderboard', label: t('leaderboard'), icon: Trophy },
  ];

  return (
    <div className="w-64 h-screen bg-[hsl(var(--sidebar-bg))] border-r border-[hsl(var(--sidebar-border))] flex flex-col overflow-y-auto fixed left-0 top-0 z-40">
      <div className="p-6 border-b border-[hsl(var(--sidebar-border))] flex items-center justify-between">
        <img 
          src="https://customer-assets.emergentagent.com/job_channel-crm-system/artifacts/4egfgzp6_ua%20logo.png" 
          alt="Underads" 
          className="h-8 cursor-pointer select-none"
          onClick={handleLogoTap}
          data-testid="sidebar-logo"
        />
        <button
          onClick={toggleTheme}
          className="w-8 h-8 rounded-lg bg-[hsl(var(--surface-elevated))] flex items-center justify-center text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))] hover:bg-[hsl(var(--surface-overlay))] transition-all"
          data-testid="theme-toggle-sidebar"
        >
          {isDark ? <Sun size={16} /> : <Moon size={16} />}
        </button>
      </div>

      <nav className="flex-1 p-4 space-y-2">
        <Button
          variant="ghost"
          className={`w-full justify-start ${
            location.pathname === '/dashboard'
              ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/20 hover:text-[hsl(var(--primary))]' 
              : 'text-[hsl(var(--sidebar-text))] hover:bg-[hsl(var(--sidebar-hover))] hover:text-[hsl(var(--sidebar-text-active))]'
          }`}
          onClick={() => navigate('/dashboard')}
          data-testid="nav-dashboard"
        >
          <LayoutDashboard size={20} className="mr-3" />
          {t('dashboard')}
        </Button>

        <div>
          <Button
            variant="ghost"
            className={`w-full justify-start ${
              isIBManagementActive
                ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/20 hover:text-[hsl(var(--primary))]' 
                : 'text-[hsl(var(--sidebar-text))] hover:bg-[hsl(var(--sidebar-hover))] hover:text-[hsl(var(--sidebar-text-active))]'
            }`}
            onClick={() => setIbExpanded(!ibExpanded)}
            data-testid="nav-ib-management"
          >
            <List size={20} className="mr-3" />
            {t('ibManagement')}
            {ibExpanded ? <ChevronDown size={16} className="ml-auto" /> : <ChevronRight size={16} className="ml-auto" />}
          </Button>

          {ibExpanded && (
            <div className="ml-4 mt-2 space-y-1 border-l-2 border-[hsl(var(--sidebar-border))] pl-2">
              <Button
                variant="ghost"
                size="sm"
                className={`w-full justify-start text-xs ${
                  location.pathname === '/ib-management/overview'
                    ? 'bg-[hsl(var(--primary))]/20 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/30 hover:text-[hsl(var(--primary))]'
                    : 'text-[hsl(var(--sidebar-text))] hover:bg-[hsl(var(--sidebar-hover))] hover:text-[hsl(var(--sidebar-text-active))]'
                }`}
                onClick={() => navigate('/ib-management/overview')}
                data-testid="nav-ib-overview"
              >
                <BarChart3 size={14} className="mr-2" />
                Overall Performance
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className={`w-full justify-start text-xs ${
                  location.pathname === '/ib-management/list'
                    ? 'bg-[hsl(var(--primary))]/20 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/30 hover:text-[hsl(var(--primary))]'
                    : 'text-[hsl(var(--sidebar-text))] hover:bg-[hsl(var(--sidebar-hover))] hover:text-[hsl(var(--sidebar-text-active))]'
                }`}
                onClick={() => navigate('/ib-management/list')}
                data-testid="nav-list-of-ib"
              >
                <List size={14} className="mr-2" />
                List of IB
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className={`w-full justify-start text-xs ${
                  location.pathname === '/ib-management/campaigns'
                    ? 'bg-[hsl(var(--primary))]/20 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/30 hover:text-[hsl(var(--primary))]'
                    : 'text-[hsl(var(--sidebar-text))] hover:bg-[hsl(var(--sidebar-hover))] hover:text-[hsl(var(--sidebar-text-active))]'
                }`}
                onClick={() => navigate('/ib-management/campaigns')}
                data-testid="nav-campaigns"
              >
                <FileText size={14} className="mr-2" />
                List of Campaign
              </Button>
            </div>
          )}
        </div>

        {/* Team Space Group */}
        <div>
          <Button
            variant="ghost"
            className={`w-full justify-start ${
              isTeamActive
                ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/20 hover:text-[hsl(var(--primary))]' 
                : 'text-[hsl(var(--sidebar-text))] hover:bg-[hsl(var(--sidebar-hover))] hover:text-[hsl(var(--sidebar-text-active))]'
            }`}
            onClick={() => setTeamExpanded(!teamExpanded)}
            data-testid="nav-team-space"
          >
            <Users size={20} className="mr-3" />
            {t('teamSpace')}
            {teamExpanded ? <ChevronDown size={16} className="ml-auto" /> : <ChevronRight size={16} className="ml-auto" />}
          </Button>

          {teamExpanded && (
            <div className="ml-4 mt-2 space-y-1 border-l-2 border-[hsl(var(--sidebar-border))] pl-2">
              {teamItems.map((item) => (
                <Button
                  key={item.path}
                  variant="ghost"
                  size="sm"
                  className={`w-full justify-start text-xs ${
                    location.pathname === item.path
                      ? 'bg-[hsl(var(--primary))]/20 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/30 hover:text-[hsl(var(--primary))]'
                      : 'text-[hsl(var(--sidebar-text))] hover:bg-[hsl(var(--sidebar-hover))] hover:text-[hsl(var(--sidebar-text-active))]'
                  }`}
                  onClick={() => navigate(item.path)}
                  data-testid={`nav-${item.path.replace(/\//g, '-').slice(1)}`}
                >
                  <item.icon size={14} className="mr-2" />
                  {item.label}
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* UA Resource Library */}
        <Button
          variant="ghost"
          className={`w-full justify-start ${
            location.pathname === '/resources'
              ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/20 hover:text-[hsl(var(--primary))]' 
              : 'text-[hsl(var(--sidebar-text))] hover:bg-[hsl(var(--sidebar-hover))] hover:text-[hsl(var(--sidebar-text-active))]'
          }`}
          onClick={() => navigate('/resources')}
          data-testid="nav-resources"
        >
          <BookOpen size={20} className="mr-3" />
          {t('uaResourceLibrary')}
        </Button>

        {/* AI Agent */}
        <Button
          variant="ghost"
          className={`w-full justify-start ${
            location.pathname === '/ai-agent'
              ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/20 hover:text-[hsl(var(--primary))]' 
              : 'text-[hsl(var(--sidebar-text))] hover:bg-[hsl(var(--sidebar-hover))] hover:text-[hsl(var(--sidebar-text-active))]'
          }`}
          onClick={() => navigate('/ai-agent')}
          data-testid="nav-ai-agent"
        >
          <Bot size={20} className="mr-3" />
          AI Agent
        </Button>

        <Button
          variant="ghost"
          className={`w-full justify-start ${
            location.pathname === '/settings'
              ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/20 hover:text-[hsl(var(--primary))]' 
              : 'text-[hsl(var(--sidebar-text))] hover:bg-[hsl(var(--sidebar-hover))] hover:text-[hsl(var(--sidebar-text-active))]'
          }`}
          onClick={() => navigate('/settings')}
          data-testid="nav-settings"
        >
          <Settings size={20} className="mr-3" />
          {t('settings')}
        </Button>
      </nav>

      {/* Online Users */}
      <div className="px-3 py-2 border-t border-[hsl(var(--sidebar-border))]">
        <p className="text-[10px] font-semibold text-[hsl(var(--sidebar-text))] uppercase tracking-wider mb-2 px-1 flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          Online ({displayOnline.length})
        </p>
        <div className="space-y-0.5 max-h-28 overflow-y-auto" data-testid="sidebar-online-users">
          {displayOnline.map((u) => (
            <div key={u.user_id} className="flex items-center gap-2 px-1 py-1 rounded-md">
              <div className="relative flex-shrink-0">
                {u.avatar_url ? (
                  <img src={u.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover" />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-[8px] font-bold">
                    {u.name?.charAt(0) || '?'}
                  </div>
                )}
                <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-emerald-400 rounded-full border border-[hsl(var(--sidebar-bg))]" />
              </div>
              <span className="text-xs text-[hsl(var(--sidebar-text))] truncate">
                {u.name}{u.user_id === currentUser.id ? ' (Anda)' : ''}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="p-4 border-t border-[hsl(var(--sidebar-border))]">
        <Button
          variant="ghost"
          className="w-full justify-start text-[hsl(var(--sidebar-text))] hover:bg-[hsl(var(--sidebar-hover))] hover:text-[hsl(var(--sidebar-text-active))]"
          onClick={handleLogout}
          data-testid="nav-logout"
        >
          <LogOut size={20} className="mr-3" />
          {t('logout')}
        </Button>
      </div>

      {/* Secret Admin PIN Modal */}
      <Dialog open={showPinModal} onOpenChange={setShowPinModal}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] text-[hsl(var(--text-primary))] max-w-xs">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-center justify-center">
              <Shield size={20} className="text-[hsl(var(--primary))]" />
              Admin Access
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-xs text-[hsl(var(--text-muted))] text-center">Masukkan PIN untuk akses Admin Panel</p>
            <Input 
              type="password"
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handlePinSubmit()}
              placeholder="••••••"
              className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-[hsl(var(--text-primary))] text-center text-lg tracking-widest"
              maxLength={10}
              autoFocus
            />
            <Button 
              onClick={handlePinSubmit}
              className="w-full bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--brand-orange))] text-white"
            >
              <Lock size={14} className="mr-2" />
              Masuk
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
